import {
  ApplicationDetailLayout,
  CircularLoader,
  EntityHeader,
  ErrorPageLayout,
} from '@converge/ui';
import { ConvergeMatchUrls, useConvergeNavigation } from '@converge/ui';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { NavigationItem, Policy, useGetPoliciesQuery } from '@converge/graphql';
import { Outlet, useMatch, useParams } from 'react-router-dom';

import { POLICY_ENTITY_DETAIL_MENU_ITEMS } from '@converge/data';

interface PolicyDetailParams {
  policyUuid: string;
}

const PolicyDetail = () => {
  const {
    selectedLeftNavItem,
    setSelectedLeftNavItem,
    leftNavItems,
    setLeftNavItems,
    sectionId,
    policyUuid,
  } = useNav();

  const {
    loading: policyEntityLoading,
    data: policyEntityData,
    error: policyEntityError,
  } = useGetPoliciesQuery({ filters: { policyUuid } });
  useEffect(() => {
    if (sectionId) {
      const find = leftNavItems.find((e) => e.value === sectionId);
      if (find) {
        setSelectedLeftNavItem(find);
      }
    }
  }, [sectionId, setSelectedLeftNavItem, leftNavItems]);

  if (policyEntityLoading) {
    return <CircularLoader className="circular-loader-style" />;
  }

  if (policyEntityError) {
    return (
      <ErrorPageLayout
        message={`Error getting insured entity: ${policyEntityError.message}`}
        messageId="policy.detail.page.error"
      />
    );
  }

  if (!policyEntityData) {
    return (
      <ErrorPageLayout
        message="Insured Entity not found."
        messageId="policy.detail.page.policyEntityNotFound"
      />
    );
  }

  return (
    <PoliciesEntityDetailInner
      policyEntity={policyEntityData.policies.edges[0].node}
      selectedLeftNavItem={selectedLeftNavItem}
      setSelectedLeftNavItem={setSelectedLeftNavItem}
      leftNavItems={leftNavItems}
      setLeftNavItems={setLeftNavItems}
      sectionId={sectionId}
    />
  );
};

interface IPolicyEntityDetailInnerProps {
  policyEntity: Policy;
  selectedLeftNavItem: NavigationItem;
  setSelectedLeftNavItem: (item: NavigationItem) => void;
  leftNavItems: NavigationItem[];
  setLeftNavItems: (item: NavigationItem[]) => void;
  sectionId?: string;
}

const PoliciesEntityDetailInner = ({
  policyEntity,
  leftNavItems,
  selectedLeftNavItem,
  setSelectedLeftNavItem,
}: IPolicyEntityDetailInnerProps) => {
  const navigate = useConvergeNavigation();

  return (
    <Fragment>
      <EntityHeader referenceId={policyEntity.referenceId} title="Policy" />
      <ApplicationDetailLayout
        leftNavItems={leftNavItems}
        onLeftNavItemSelected={(item) => {
          setSelectedLeftNavItem(item);
          navigate.policyEntityDetail({
            policyUuid: policyEntity?.uuid as string,
            sectionUrl: item.value,
          });
        }}
        selectedLeftNavItem={selectedLeftNavItem}
      >
        <Outlet
          context={{
            policyUuid: policyEntity?.uuid as string,
            policyDetails: policyEntity,
            selectedLeftNavItem,
          }}
        />
      </ApplicationDetailLayout>
    </Fragment>
  );
};

export default PolicyDetail;

const useNav = () => {
  const { policyUuid } = useParams<keyof PolicyDetailParams>();

  let sectionId = useMatch(ConvergeMatchUrls.policyEntityDetail())?.params[
    'sectionId'
  ];

  const { defaultSelectedLeftNavItem } = useMemo(() => {
    return {
      defaultSelectedLeftNavItem:
        POLICY_ENTITY_DETAIL_MENU_ITEMS.find(
          (item) => item.value === sectionId
        ) || POLICY_ENTITY_DETAIL_MENU_ITEMS[0],
    };
  }, [sectionId]);

  const [selectedLeftNavItem, setSelectedLeftNavItem] =
    useState<NavigationItem>(defaultSelectedLeftNavItem);
  const [leftNavItems, setLeftNavItems] = useState<NavigationItem[]>(
    POLICY_ENTITY_DETAIL_MENU_ITEMS
  );

  return {
    selectedLeftNavItem,
    setSelectedLeftNavItem,
    leftNavItems,
    setLeftNavItems,
    sectionId,
    policyUuid,
  };
};
